import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {router, usePage} from '@inertiajs/react';
import {Container} from 'react-bootstrap';
import axios from 'axios';
import HelpTooltip from '../../Components/HelpTooltip';
import OeTip from '../../Components/OrderEditor/OeTip';

/**
 * Einstiegsseite des Auftrags-Editors (PLAN.md 3.1, ABGLEICH E7–E18, K44).
 *
 * Sie ist der Nachbau der Belegleiste des Originalprogramms: zwei Tabs
 * („Aufträge" / „Lieferscheine"), ein Nummernfeld mit Präfix-Logik, der Knopf
 * „Laden" mit Statuszeile und darunter die Chips „Zuletzt:" mit ihren
 * To-do-Markern. Geladen wird nicht hier — die Seite löst die Nummer nur in
 * eine weclapp-ID auf und wechselt dann auf die Editor-Seite.
 *
 * Abweichung vom Original, bewusst: Das automatische Öffnen des ersten Chips
 * hängt an `?auto=1` statt an jedem Aufruf. Über das Menü aufgerufen soll die
 * Seite zuerst zeigen, was offen ist.
 *
 * Optik und Beschriftungen stammen aus dem Original; das Stylesheet dazu liegt
 * unter `.oe-root` (order-editor.scss) und wird hier nur benutzt.
 */

/** Platzhalter und Präfix je Belegart (ABGLEICH E9/E10). */
const MODES = {
    order: {
        label: 'Aufträge',
        prefix: 'AB',
        placeholder: 'Auftragsnummer, z. B. AB1490',
    },
    shipment: {
        label: 'Lieferscheine',
        prefix: 'LS',
        placeholder: 'Liefernummer, z. B. LS1422',
    },
};

/**
 * Eingabe normalisieren wie `load()` im Original (ABGLEICH E10/E11):
 * reine Ziffern bekommen das Präfix des aktiven Tabs, sonst werden die
 * führenden Buchstaben großgeschrieben.
 */
export function normalisiereNummer(eingabe, mode) {
    const roh = String(eingabe || '').trim().replace(/\s+/g, '');

    if (!roh) {
        return '';
    }

    if (/^\d+$/.test(roh)) {
        return MODES[mode].prefix + roh;
    }

    return roh.replace(/^[a-zA-Z]+/, (treffer) => treffer.toUpperCase());
}

/**
 * Belegart aus der fertigen Nummer (ABGLEICH E12). Wie im Original entscheidet
 * allein das Präfix, und zwar in beide Richtungen: „LS…" ist ein Lieferschein,
 * alles andere ein Auftrag. Der aktive Tab wird dabei überstimmt — seine
 * Wirkung hatte er schon in `normalisiereNummer`, wo er das Präfix für reine
 * Ziffern liefert.
 */
export function belegartFuer(nummer) {
    return /^LS/i.test(String(nummer || '')) ? 'shipment' : 'order';
}

/**
 * Grober To-do-Marker am Chip (ABGLEICH E16/E17). Er speist sich allein aus
 * dem weclapp-Status, der ohne zusätzlichen Abruf mit der Liste kommt — eine
 * ehrliche Grob-Ampel, keine Wizard-Checkliste. Fehlt der Status, zeigt der
 * Chip gar keinen Marker.
 */
export function todoMarker(eintrag, mode) {
    if (!eintrag || !eintrag.status) {
        return null;
    }

    if (mode === 'order') {
        if (eintrag.status === 'ORDER_ENTRY_IN_PROGRESS') {
            return {
                cls: 'draft',
                text: '✎ Entwurf',
                tip: 'Noch in Erfassung — Auftragsbestätigung nicht erzeugt.',
            };
        }

        if (eintrag.shipped) {
            return {cls: 'done', text: '✓ geliefert', tip: 'Komplett geliefert.'};
        }

        return {
            cls: 'open',
            text: '● offen',
            tip: 'Auftragsbestätigung erzeugt, noch nicht komplett geliefert.',
        };
    }

    if (eintrag.status === 'NEW') {
        return {cls: 'open', text: '● offen', tip: 'Lieferschein noch nicht festgeschrieben.'};
    }

    return {cls: 'done', text: '✓', tip: 'Festgeschrieben oder versendet.'};
}

/** Kundenname am Chip auf 22 Zeichen kürzen (ABGLEICH E15). */
export function kurzerKundenname(name) {
    return String(name || '').slice(0, 22);
}

/** Ziel-Adresse im Editor für einen aufgelösten Beleg. */
export function editorZiel(treffer) {
    if (treffer.type === 'shipment') {
        return `/order-editor/weclapp/${treffer.salesOrderId}`
            + `?type=shipment&number=${encodeURIComponent(treffer.number)}`;
    }

    return `/order-editor/weclapp/${treffer.id}`;
}

export default function Start() {
    const {auto, settings = {}, bearbeiter} = usePage().props;

    const [mode, setMode] = useState('order');
    const [nummer, setNummer] = useState('');
    const [status, setStatus] = useState({art: 'ok', text: ''});
    const [recent, setRecent] = useState(null);
    const [laeuft, setLaeuft] = useState(false);

    const feld = useRef(null);
    const autoErledigt = useRef(false);

    const modeHint = settings.writeMode === 'dry-run'
        ? 'Trockenlauf — es wird nichts nach weclapp geschrieben.'
        : (settings.writeMode === 'test-orders' ? 'Testmodus — nur Testaufträge.' : null);

    const liste = useMemo(() => (recent && recent[mode]) || [], [recent, mode]);

    /**
     * Nummer auflösen und auf die Editor-Seite wechseln. Ein Chip nimmt
     * denselben Weg wie der Knopf „Laden" — im Original tut er das auch
     * (`$('#num').value = …; load();`).
     */
    const laden = useCallback(async (eingabe) => {
        const roh = eingabe === undefined ? nummer : eingabe;
        const fertig = normalisiereNummer(roh, mode);

        if (!fertig) {
            return;
        }

        // Die fertige Nummer steht im Feld, bevor der Abruf losgeht — der
        // Bearbeiter sieht, womit gearbeitet wird.
        setNummer(fertig);

        const ziel = belegartFuer(fertig);

        if (ziel !== mode) {
            setMode(ziel);
        }

        setLaeuft(true);
        setStatus({art: 'work', text: 'lädt…'});

        try {
            const {data} = await axios.get('/order-editor/resolve', {
                params: {type: ziel, number: fertig},
            });

            setStatus({art: 'ok', text: ''});
            router.visit(editorZiel(data));
        } catch (fehler) {
            const text = fehler.response?.data?.message
                || fehler.response?.data?.error
                || 'Der Beleg konnte nicht geladen werden.';

            setStatus({art: 'err', text});
            setLaeuft(false);
        }
    }, [mode, nummer]);

    // Chips holen. Sie kommen aus der Nachweistabelle des Ordermanagements
    // und aus weclapp; scheitert beides, bleibt die Reihe einfach leer.
    useEffect(() => {
        let abgebrochen = false;

        axios.get('/order-editor/recent')
            .then(({data}) => {
                if (!abgebrochen) {
                    setRecent(data || {order: [], shipment: []});
                }
            })
            .catch(() => {
                if (!abgebrochen) {
                    setRecent({order: [], shipment: []});
                }
            });

        return () => {
            abgebrochen = true;
        };
    }, []);

    // Auto-Öffnen des ersten Chips (ABGLEICH E18), nur mit `?auto=1`.
    useEffect(() => {
        if (!auto || autoErledigt.current || recent === null) {
            return;
        }

        const ersteNummer = liste.length ? liste[0].number : null;

        if (ersteNummer) {
            autoErledigt.current = true;
            laden(ersteNummer);
        }
    }, [auto, recent, liste, laden]);

    /**
     * Tab-Wechsel (ABGLEICH E7/E8): Belegart umstellen, Eingabe und
     * Statuszeile leeren, Fokus zurück ins Feld. Einen Belegzustand gibt es
     * auf dieser Seite nicht — der lebt auf der Editor-Seite.
     */
    const wechsleModus = (m) => {
        if (m === mode) {
            return;
        }

        setMode(m);
        setNummer('');
        setStatus({art: 'ok', text: ''});
        autoErledigt.current = true;

        if (feld.current) {
            feld.current.focus();
        }
    };

    return (
        <Container fluid className="pt-2">
            <div className="oe-root">
                {/* Kein eigener Seitenkopf: Logo, Benutzer und Menü stellt der
                    Rahmen des Ordermanagements. */}
                <main className="oe-main">
                    {modeHint ? (
                        <div className="oe-modebar">
                            <span>⚠</span>
                            <span>{modeHint}</span>
                            <HelpTooltip id="oe-start-modebar-help" placement="bottom" size={0.75}>
                                Die Betriebsart steht in den Systemeinstellungen. Im Trockenlauf werden
                                Schreibvorgänge nur protokolliert, im Testmodus nur an Testaufträgen zugelassen.
                            </HelpTooltip>
                        </div>
                    ) : null}

                    <div className="bar">
                        <span className="tabs" role="group" aria-label="Belegart">
                            {Object.keys(MODES).map((m) => (
                                <OeTip
                                    key={m}
                                    tip={m === 'order'
                                        ? 'Aufträge bearbeiten: Positionen, Rabatte, Lager, Lieferscheine — der volle Wizard.'
                                        : 'Lieferscheine bearbeiten: Mengen senken und Positionen entfernen; hinzufügen geht nur über den Auftrag.'}
                                >
                                    <button
                                        type="button"
                                        className={mode === m ? 'on' : undefined}
                                        aria-pressed={mode === m}
                                        onClick={() => wechsleModus(m)}
                                    >
                                        {MODES[m].label}
                                    </button>
                                </OeTip>
                            ))}
                        </span>

                        <OeTip tip={mode === 'order'
                            ? 'Auftragsnummer eingeben. Eine reine Zahl bekommt das Präfix „AB" — 1490 wird zu AB1490. Eine „LS…"-Nummer wechselt selbsttätig auf Lieferscheine.'
                            : 'Liefernummer eingeben. Eine reine Zahl bekommt das Präfix „LS" — 1422 wird zu LS1422.'}
                        >
                            <input
                                ref={feld}
                                type="text"
                                value={nummer}
                                style={{width: 240}}
                                autoFocus
                                aria-label={MODES[mode].placeholder}
                                placeholder={MODES[mode].placeholder}
                                onChange={(e) => setNummer(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        laden();
                                    }
                                }}
                            />
                        </OeTip>

                        <OeTip tip="Sucht den Beleg in weclapp und öffnet ihn im Auftrags-Editor. Dauert ein bis zwei Sekunden.">
                            <button
                                type="button"
                                className="primary"
                                disabled={laeuft || !nummer.trim()}
                                onClick={() => laden()}
                            >
                                Laden
                            </button>
                        </OeTip>

                        <span className={`status ${status.art}`} role="status">{status.text}</span>

                        {/* `.sp` (Dehnfuge) ist im Stylesheet nur für die Kopfleiste
                            gesetzt — in der Belegleiste steht sie deshalb hier. */}
                        <span style={{flex: 1}}/>

                        <HelpTooltip
                            id="oe-start-help"
                            placement="left"
                            size={0.9}
                            detail={
                                <div className="text-start">
                                    Aufträge, die aus dem Shop stammen, lassen sich zusätzlich direkt aus der
                                    Bestellung öffnen; Belege aus weclapp über die Zeilenaktion in den Listen
                                    „Verkauf – Aufträge" und „Verkauf – Lieferungen".
                                </div>
                            }
                        >
                            <div className="text-start">
                                <strong>Auftrags-Editor</strong>
                                <div className="mt-1">
                                    Belegnummer eingeben oder einen der zuletzt bearbeiteten Belege anklicken.
                                </div>
                            </div>
                        </HelpTooltip>
                    </div>

                    {liste.length > 0 ? (
                        <div className="recent">
                            <span className="muted">Zuletzt:</span>
                            {liste.map((eintrag) => {
                                const marker = todoMarker(eintrag, mode);
                                const kunde = kurzerKundenname(eintrag.customer);

                                return (
                                    <OeTip
                                        key={eintrag.number}
                                        tip={
                                            <>
                                                {eintrag.customer ? <div>{eintrag.customer}</div> : null}
                                                {marker ? <div>{marker.tip}</div> : null}
                                                <div>Öffnet den Beleg im Auftrags-Editor.</div>
                                            </>
                                        }
                                    >
                                        <button
                                            type="button"
                                            className="chip"
                                            disabled={laeuft}
                                            onClick={() => laden(eintrag.number)}
                                        >
                                            {eintrag.number}
                                            {kunde ? ` · ${kunde}` : ''}
                                            {marker ? (
                                                <span className={`rtodo ${marker.cls}`}>{marker.text}</span>
                                            ) : null}
                                        </button>
                                    </OeTip>
                                );
                            })}
                            <HelpTooltip
                                id="oe-start-recent-help"
                                placement="right"
                                size={0.8}
                                detail={
                                    <div className="text-start">
                                        Quelle: der Nachweis des Auftrags-Editors und weclapp (dort angelegt oder im
                                        Status weitergeschoben). Der Marker ist eine Grob-Ampel aus dem
                                        weclapp-Status, keine Wizard-Checkliste.
                                    </div>
                                }
                            >
                                <div className="text-start">
                                    Die fünf Belege dieser Belegart, die zuletzt bearbeitet wurden.
                                </div>
                            </HelpTooltip>
                        </div>
                    ) : null}
                </main>
            </div>
        </Container>
    );
}
